'use client';

import React, { useEffect } from 'react';
import { useStripe } from '@stripe/react-stripe-js';

import useCheckoutState from './hooks/useCheckoutState';
import { checkoutTypes } from './enums/checkout-types';
import useTrackVisit from './hooks/useTrackVisit';
import useCheckoutHelpers from './hooks/useCheckoutHelpers';
import useEventTracking from './hooks/useEventTracking';
import CheckoutCenter from './layouts/CheckoutCenter';
import CheckoutSplit from './layouts/CheckoutSplit';
import CheckoutDouble from './layouts/CheckoutDouble';
import CheckoutDoubleBanner from './layouts/CheckoutDoubleBanner';
import Providers from './providers';
import TemplateDialog from './components/TemplateDialog';
import useCheckoutRouter from '@repo/checkout/hooks/useCheckoutRouter';
import api from './api';
import getSessionId from './lib/getSessionId';
import mapFieldsToState from './lib/mapFieldsToState';
import getVariantsFormFields from './lib/getVariantsFormFields';
import { getTicketGroupsFormState } from './lib/getTicketGroupsFormState';

const Checkout = () => {
  const { checkoutLayout, checkoutType, isConfirmation } = useCheckoutState();
  useTrackVisit();
  const router = useCheckoutRouter();
  const { trackEnterEvent } = useEventTracking();

  // handle bank redirects (iDEAL, Bancontact, etc.)
  const stripe = useStripe();
  const { processPaymentIntent } = useCheckoutHelpers();

  useEffect(() => {
    // if this is a confirmation page, we've already processed the payment intent
    if (isConfirmation) return;

    // wait until stripe is initialized
    if (!stripe) return;

    const paymentIntent = router.search.get('payment_intent');
    const paymentIntentClientSecret = router.search.get('payment_intent_client_secret');

    if (paymentIntent && paymentIntentClientSecret) {
      processPaymentIntent(paymentIntentClientSecret);
    }
  }, [router.search, stripe, isConfirmation]);

  // track enter event
  useEffect(() => {
    if (!trackEnterEvent) return;

    trackEnterEvent();
  }, [trackEnterEvent]);

  if (checkoutType === checkoutTypes.CHECKOUT || checkoutType === checkoutTypes.IFRAME) {
    switch (checkoutLayout) {
      case 'split':
        return <CheckoutSplit />;
      case 'double':
        return <CheckoutDouble />;
      case 'double_banner':
        return <CheckoutDoubleBanner />;
      case 'center':
      default:
        return <CheckoutCenter />;
    }
  } else {
    return <CheckoutCenter />;
  }
};

const CheckoutWrapper = () => {
  return (
    <Providers>
      <Checkout />

      <TemplateDialog />
    </Providers>
  );
};

export default CheckoutWrapper;
